import { useEffect, useRef, useState } from 'react';

import type { VerticalScanConfig } from '../winchMqttApi';

export type ScanRange = { minDepth: number; maxDepth: number };

/**
 * The device's vertical-scan topics are the source of truth: a scan may be started from this
 * portal or locally on the winch. `scanRunning` mirrors `mqttScan.enabled`; the local toggle
 * only opens scan mode for editing and Start.
 */
export function useWinchScanUi(input: {
    mqttScan: VerticalScanConfig | null;
    setLimitsFailed: boolean;
}): {
    scanRange: ScanRange | null;
    setScanRange: (range: ScanRange | null) => void;
    scanEnabled: boolean;
    setScanEnabled: (enabled: boolean) => void;
    scanRunning: boolean;
} {
    const { mqttScan, setLimitsFailed } = input;
    const [scanRange, setScanRange] = useState<ScanRange | null>(null);
    const [scanModeOn, setScanEnabled] = useState(false);
    const scanRunning = mqttScan?.enabled === true;
    const wasRunningRef = useRef(scanRunning);

    useEffect(() => {
        if (mqttScan == null) {
            return;
        }
        setScanRange({ minDepth: mqttScan.yMin, maxDepth: mqttScan.yMax });
    }, [mqttScan]);

    useEffect(() => {
        if (wasRunningRef.current && !scanRunning) {
            setScanEnabled(false);
        }
        wasRunningRef.current = scanRunning;
    }, [scanRunning]);

    useEffect(() => {
        if (!setLimitsFailed) {
            return;
        }
        if (mqttScan == null) {
            setScanRange(null);
            return;
        }
        setScanRange({ minDepth: mqttScan.yMin, maxDepth: mqttScan.yMax });
    }, [setLimitsFailed, mqttScan]);

    return {
        scanRange,
        setScanRange,
        scanEnabled: scanRunning || scanModeOn,
        setScanEnabled,
        scanRunning,
    };
}
